import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import useSWR from "swr";
import {
  getSanctumUser,
  sanctumCsrf,
  signIn,
  logout as userLogout,
  signInWithGoogleCallback,
} from "@/services/api/User";
import toast from "react-hot-toast";
import { getCookie, deleteCookie, setCookie } from 'cookies-next';
import { env } from "@/next.config";
import { getStartupDetailSimple, updateStartup, createStartupByUser } from "@/services/api/Startup";
import { checkEmailDomainWithList as checkEmailDomain } from "@/utils/checkEmailDomain";
import { updateHeaders } from "@/utils/httpUtils";
import { useAtom } from "jotai";
import { loginPopup, purchasePopup, userAtom } from "@/Atoms";

const UNICORN_TRACKER_INTRO = "/unicorn-tracker/intro";
const UNICORN_TRACKER = "/unicorn-tracker";

const useAuth = function ({ middleware, redirectIfAuthenticated } = {}) {
  const router = useRouter();
  const [openLoginPopup, setOpenLoginPopup] = useAtom(loginPopup);
  const [openPurchasePopup, setOpenPurchasePopup] = useAtom(purchasePopup);
  const [userState, setUserState] = useAtom(userAtom);
  const [loginError, setLoginError] = useState("");

  const [isLoading, setLoading] = useState(false);
  redirectIfAuthenticated =
    redirectIfAuthenticated === null || redirectIfAuthenticated === undefined
      ? true
      : redirectIfAuthenticated;

  const {
    data: user,
    error,
    mutate,
  } = useSWR("/user", () => getSanctumUser().then((response) => response.data));

  const csrf = () => sanctumCsrf();

  const login = async ({ setErrors, startup, purpose, notRedirect, ...props }) => {
    setErrors([]);
    setLoading(true);

    let redirect = ''

    await csrf();

    try {
      const response = await signIn(props);

      mutate();
      const { user } = response.data;
      user.roles = user?.roles === 'admin' ? 'admin-team' : user?.roles
      setUserState(user)

      if (startup) {
        const access_token = response.data.access_token;
        updateHeaders({
          "Authorization": `Bearer ${access_token}`
        });

        const hasSameEmailDomain = await checkEmailDomain(user.email, startup.website);

        if (purpose === 'claim' && startup.id) {
          const payload = {
            name: startup.name,
            website: startup.website,
            twitter_url: startup.twitter_url,
            linkedin_url: startup.linkedin_url,
            logo_url: startup.logo_url,
            country_id: startup.country_id,
            creator_id: user.id,
            summary: startup.summary,
            claimed: 1,
            owner_id: user.id,
            owner_email: hasSameEmailDomain ? user.email : '',
          };

          const responseClaim = await updateStartup(startup.id, payload);
          localStorage.setItem('startup-event', 'claim')
        }

        if (purpose === 'register') {
          if (!startup.name || startup.name.trim().length < 3) {
            toast.error("Startup name must be at least 3 characters!");
            return;
          }

          const payload = {
            name: startup.name,
            website: startup.website,
            owner_id: user.id,
            owner_email: hasSameEmailDomain ? user.email : '',
            creator_id: user.id
          };

          const responseRegister = await createStartupByUser(payload);
          localStorage.setItem('startup-event', 'register')
          redirect = `/startups/${responseRegister.data.data.slug}-${responseRegister.data.data.id}`
        }
      }


      if (process.env.ENV == 'local') {
        setCookie('dev-token', response.data?.access_token)
      }

      if (!notRedirect) {
        // --- Start Redirect Logic --- 
        const specificRedirect = localStorage.getItem('loginRedirectTo');

        if (specificRedirect) {
          console.log(`[Auth Hook] Found specificRedirect in localStorage: ${specificRedirect}`);
          localStorage.removeItem('loginRedirectTo'); // Use it once
          location.href = specificRedirect;
        }
        // Fallback 1: Startup related redirect
        else if (redirect) {
          console.log(`[Auth Hook] Using startup redirect: ${redirect}`);
          location.href = redirect;
        }
        // Fallback 2: Query param redirect
        else if (router.query.redirect_to) {
          console.log(`[Auth Hook] Using query param redirect_to: ${router.query.redirect_to}`);
          location.href = router.query.redirect_to;
        }
        // Fallback 3: Default redirect
        else {
          console.log(`[Auth Hook] Using default redirect logic.`);
          setOpenLoginPopup(false);
          if (router.pathname === "/auth/login") {
            location.href = "/";
          }
          // Logging in from the Unicorn Tracker landing page means the user
          // wanted the tracker itself — reloading would just leave them on
          // the landing page with no sign anything happened.
          else if (router.pathname === UNICORN_TRACKER_INTRO) {
            location.href = UNICORN_TRACKER;
          }
          // Consider removing location.reload() if it causes issues, maybe router.push('/')?
          // However, using location.href might be necessary after auth changes.
          else {
            location.reload();
          }
        }
        // --- End Redirect Logic ---
      }
    } catch (error) {
      if (!error.response) {
        throw error;
      }

      if (error.response && error.response.data) {
        setLoginError(error.response.data.message)
      }
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      await sanctumCsrf();
      const response = await signInWithGoogleCallback(router.query);
      mutate();

      const { user } = response.data;
      user.roles = user?.roles === 'admin' ? 'admin-team' : user?.roles
      setUserState(user);

      let redirectTo = getCookie('google-url-redirect-to')
      if (redirectTo) {
        deleteCookie('google-url-redirect-to')
      }

      // Same as the email/password path: signing in from the tracker landing
      // page should land the user on the tracker, not back on the landing page.
      if (redirectTo && new URL(redirectTo, location.origin).pathname === UNICORN_TRACKER_INTRO) {
        redirectTo = UNICORN_TRACKER
      }

      if (!user.role) {
        const target = redirectTo
          ? `/register/google-onboarding?redirect_to=${encodeURIComponent(redirectTo)}`
          : '/register/google-onboarding'
        location.href = target
      } else if (redirectTo) {
        location.href = redirectTo
      } else {
        location.href = '/'
      }
    } catch (error) {
      setUserState(null);
      deleteCookie('google-url-redirect-to');
      const message = error?.response?.data?.error || 'Sign in with Google failed. Please try again.';
      toast.error(message);
      router.push('/auth/login');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    const loadingToast = toast.loading('Logging out...');
    try {
      if (process.env.ENV == 'local') {
        deleteCookie('dev-token')
      }

      await userLogout();

      toast.success("You've logged out!", { id: loadingToast });
      location.href = "/"
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error("Logout failed. Please try again.", { id: loadingToast });
    }
  };

  useEffect(() => {
    if (user || error) {
      setLoading(false);
    }

    if (middleware == "guest" && user && redirectIfAuthenticated)
      router.push("/");
    if (middleware == "auth" && !user && error)
      router.push(`/auth/login?redirect_to=${router.asPath}`);
    if (middleware == "admin" && error || (middleware === "admin" && user && user.roles !== 'admin' && user.roles !== 'admin-team')) {
      router.push("/");
    }

    if (middleware == "auth-popup" && !user && error)
      setOpenLoginPopup(true)
  }, [user, error, middleware, redirectIfAuthenticated, router]);

  if (user) {
    user.roles = user?.roles === 'admin' ? 'admin-team' : user?.roles
  }

  setUserState(user)

  return {
    user,
    authError: error,
    csrf,
    login,
    logout,
    isLoading,
    signInWithGoogle,
    loginError,
    setLoginError,
    mutate
  };
};

export default useAuth;
